import React, { useState } from 'react';
import './DuplicatesModal.css';

const DuplicatesModal = ({ isOpen, onClose, duplicates, onDelete, onRefresh }) => {
  const [deletingIds, setDeletingIds] = useState(new Set());
  const [selectedToKeep, setSelectedToKeep] = useState({});

  if (!isOpen) return null;

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('sk-SK', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      weekday: 'long'
    });
  };

  const formatTime = (timestamp) => {
    if (!timestamp) return 'N/A';
    const date = new Date(timestamp);
    return date.toLocaleTimeString('sk-SK', {
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit'
    });
  };

  const getShiftName = (shift) => {
    const shiftNames = {
      'work': '💼 Práca',
      'morning': '🌅 Ranná',
      'afternoon': '☀️ Denná',
      'night': '🌙 Nočná',
      'day': '☀️ Denná',
      'home': '🏠 Domov',
      'vacation': '🏖️ Dovolenka',
      'sick': '🤒 PN',
      'holiday': '🎉 Sviatok'
    };
    return shiftNames[shift] || shift;
  };

  const handleDeleteRecord = async (recordId, date) => {
    if (!window.confirm('Naozaj chcete vymazať tento záznam?')) {
      return;
    }

    setDeletingIds(prev => new Set([...prev, recordId]));

    try {
      const response = await fetch(`/api/attendance/${recordId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });

      if (response.ok) {
        alert('Záznam bol úspešne vymazaný!');
        // Refresh the duplicates list
        if (onRefresh) {
          onRefresh();
        }
      } else {
        const error = await response.json();
        alert(`Chyba pri mazaní: ${error.error || 'Neznáma chyba'}`);
      }
    } catch (error) {
      console.error('Error deleting record:', error);
      alert('Chyba pri mazaní záznamu!');
    } finally {
      setDeletingIds(prev => {
        const newSet = new Set(prev);
        newSet.delete(recordId);
        return newSet;
      });
    }
  };

  const handleKeepOnly = (date, recordId) => {
    setSelectedToKeep(prev => ({
      ...prev,
      [date]: recordId
    }));
  };

  const handleDeleteOthers = async (date, records) => {
    const keepId = selectedToKeep[date];
    
    if (!keepId) {
      alert('Prosím vyberte záznam, ktorý chcete ponechať');
      return;
    }

    const recordsToDelete = records.filter(r => r.id !== keepId);
    
    if (!window.confirm(`Naozaj chcete vymazať ${recordsToDelete.length} ${recordsToDelete.length === 1 ? 'záznam' : 'záznamov'}?`)) {
      return;
    }

    let successCount = 0;
    let errorCount = 0;

    for (const record of recordsToDelete) {
      try {
        const response = await fetch(`/api/attendance/${record.id}`, {
          method: 'DELETE',
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`
          }
        });

        if (response.ok) {
          successCount++;
        } else {
          errorCount++;
        }
      } catch (error) {
        console.error('Error deleting record:', error);
        errorCount++;
      }
    }

    alert(`Vymazané: ${successCount}\nChyby: ${errorCount}`);
    
    // Clear selection for this date
    setSelectedToKeep(prev => {
      const newState = { ...prev };
      delete newState[date];
      return newState;
    });

    // Refresh the list
    if (onRefresh) {
      onRefresh();
    }
  };

  return (
    <div className="duplicates-modal-overlay" onClick={onClose}>
      <div className="duplicates-modal-content" onClick={(e) => e.stopPropagation()}>
        <div className="duplicates-modal-header">
          <h2>🔍 Duplicitné záznamy dochádzky</h2>
          <button className="modal-close-button" onClick={onClose}>✕</button>
        </div>

        <div className="duplicates-modal-body">
          {duplicates.length === 0 ? (
            <div className="no-duplicates">
              <p>✅ Žiadne duplicitné záznamy!</p>
              <p className="subtitle">Všetky dni majú maximálne jeden záznam dochádzky.</p>
            </div>
          ) : (
            <>
              <div className="duplicates-summary">
                <p>
                  <strong>Nájdené duplicity:</strong> {duplicates.length} {duplicates.length === 1 ? 'deň' : 'dní'}
                </p>
                <p className="subtitle">Pre každý deň vyberte záznam, ktorý chcete ponechať, a vymažte ostatné.</p>
              </div>

              <div className="duplicates-list">
                {duplicates.map((duplicate, index) => (
                  <div key={duplicate.date} className="duplicate-day-card">
                    <div className="duplicate-day-header">
                      <h3>📅 {formatDate(duplicate.date)}</h3>
                      <span className="record-count-badge">
                        {duplicate.record_count} {duplicate.record_count === 1 ? 'záznam' : 'záznamy'}
                      </span>
                    </div>

                    <div className="duplicate-records">
                      {duplicate.records.map((record, recordIndex) => (
                        <div 
                          key={record.id} 
                          className={`duplicate-record-item ${
                            selectedToKeep[duplicate.date] === record.id ? 'selected-to-keep' : ''
                          }`}
                        >
                          <div className="record-number">#{recordIndex + 1}</div>
                          
                          <div className="record-details">
                            <div className="record-detail-row">
                              <span className="detail-label">🏢 Lokalita:</span>
                              <span className="detail-value">{record.location || 'N/A'}</span>
                            </div>
                            
                            <div className="record-detail-row">
                              <span className="detail-label">⏰ Smena:</span>
                              <span className="detail-value">{getShiftName(record.shift)}</span>
                            </div>
                            
                            <div className="record-detail-row">
                              <span className="detail-label">🕐 Vytvorené:</span>
                              <span className="detail-value">{formatTime(record.created_at)}</span>
                            </div>

                            {record.project_name && (
                              <div className="record-detail-row">
                                <span className="detail-label">📁 Projekt:</span>
                                <span className="detail-value">{record.project_name}</span>
                              </div>
                            )}
                          </div>

                          <div className="record-actions">
                            <button
                              className={`btn-keep ${selectedToKeep[duplicate.date] === record.id ? 'active' : ''}`}
                              onClick={() => handleKeepOnly(duplicate.date, record.id)}
                              title="Ponechať tento záznam"
                            >
                              {selectedToKeep[duplicate.date] === record.id ? '✓ Ponechať' : '☐ Vybrať'}
                            </button>
                            
                            <button
                              className="btn-delete-single"
                              onClick={() => handleDeleteRecord(record.id, duplicate.date)}
                              disabled={deletingIds.has(record.id)}
                              title="Vymazať tento záznam"
                            >
                              {deletingIds.has(record.id) ? '⏳' : '🗑️ Vymazať'}
                            </button>
                          </div>
                        </div>
                      ))}
                    </div>

                    {selectedToKeep[duplicate.date] && (
                      <div className="duplicate-day-actions">
                        <button
                          className="btn-delete-others"
                          onClick={() => handleDeleteOthers(duplicate.date, duplicate.records)}
                        >
                          🗑️ Vymazať ostatné záznamy pre tento deň
                        </button>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </>
          )}
        </div>

        <div className="duplicates-modal-footer">
          <button className="btn-modal-close" onClick={onClose}>
            Zavrieť
          </button>
        </div>
      </div>
    </div>
  );
};

export default DuplicatesModal;
